import { USStatePostalCode } from "@redotech/redo-fulfillment-common/fulfillment-group-state-codes";
import { normalizedStateCodes } from "@redotech/redo-fulfillment-operations-common/shipping/util";
import { useTranslation } from "@redotech/redo-i18n";
import { BadgeRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoTagsDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { memo, useMemo, useState } from "react";

type ProvinceItem = BadgeRedoListItem<USStatePostalCode>;

// normalizedStateCodes is name -> code with lowercase names; invert it so the
// dropdown can show "Nevada (NV)" without a second hand-maintained list.
const STATE_NAME_BY_CODE = new Map(
  Object.entries(normalizedStateCodes).map(([name, code]) => [
    code,
    name.replace(/\b[a-z]/g, (letter) => letter.toUpperCase()),
  ]),
);

function label(code: USStatePostalCode): string {
  const name = STATE_NAME_BY_CODE.get(code);
  return name ? `${name} (${code})` : code;
}

function toItem(code: USStatePostalCode): ProvinceItem {
  return {
    id: code,
    type: "badge",
    value: code,
    badge: { text: label(code), color: "gray" },
  };
}

const ALL_PROVINCES = Object.values(USStatePostalCode);

/**
 * Multi-select over US states. Only US destinations are quotable today
 * (validateUsShipToAddress rejects the rest), so the list is US-only.
 */
export const SuppliesProvinceSelect = memo(function SuppliesProvinceSelect({
  provinces,
  setProvinces,
  disabled,
}: {
  provinces: USStatePostalCode[];
  setProvinces: (provinces: USStatePostalCode[]) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const [searchString, setSearchString] = useState("");

  const options = useMemo(() => {
    const query = searchString.trim().toLowerCase();
    const matching = query
      ? ALL_PROVINCES.filter((code) =>
          label(code).toLowerCase().includes(query),
        )
      : ALL_PROVINCES;
    return matching.map(toItem);
  }, [searchString]);

  const selectedOptions = useMemo(() => provinces.map(toItem), [provinces]);

  return (
    <RedoTagsDropdownInput
      description={t("Matches when the destination is in any of these states.")}
      disabled={disabled}
      label={t("Destination states")}
      options={options}
      placeholder={t("Select states")}
      searchPlaceholder={t("Search states...")}
      searchString={searchString}
      selectedOptions={selectedOptions}
      setSearchString={setSearchString}
      setSelectedOptions={(items) => setProvinces(items.map((i) => i.value))}
      size="xs"
    />
  );
});
